import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '@/context/LanguageContext';
import { ArrowUp, ArrowDown } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Category → symbols. Only symbols from the ~20-instrument active universe
// are surfaced; unsupported (US futures/energy futures) have been dropped.
const CATEGORY_MAP = {
  Popular: ['EURUSD', 'BTCUSD', 'XAUUSD', 'AAPL', 'GBPUSD'],
  Forex: ['EURUSD', 'GBPUSD', 'USDJPY', 'USDCHF', 'AUDUSD'],
  'Crypto CFDs': ['BTCUSD', 'ETHUSD'],
  Metals: ['XAUUSD', 'XAGUSD'],
  Indices: ['US30', 'NAS100', 'SPX500'],
  Energy: ['USOIL', 'UKOIL'],
  Shares: ['AAPL', 'MSFT', 'GOOGL', 'AMZN', 'TSLA', 'NVDA'],
};

const MarketCategoryBar = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [selectedCategory, setSelectedCategory] = useState('Popular');
  const [quotes, setQuotes] = useState({});
  const [prevQuotes, setPrevQuotes] = useState({});

  const categories = [
    { key: 'Popular', label: t('popular') },
    { key: 'Forex', label: t('forex') },
    { key: 'Crypto CFDs', label: t('cryptoCFDs') },
    { key: 'Metals', label: t('metals') },
    { key: 'Indices', label: t('indices') },
    { key: 'Energy', label: t('energy') },
    { key: 'Shares', label: t('shares') },
  ];

  useEffect(() => {
    fetchQuotes();
    // Refresh cadence: 60s (was 15s). Backend quote cache TTL is 120s, so
    // a faster poll only churns the UI without adding freshness. Only the
    // currently-selected category is fetched now — tab switches trigger a
    // fresh call, which is bounded by the shared cache.
    const interval = setInterval(fetchQuotes, 60000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCategory]);

  const fetchQuotes = async () => {
    // Only fetch symbols for the currently-visible category. Previously we
    // fetched the union of ALL categories (19 symbols), which pinned the
    // shared cache warm for symbols nobody was looking at. Now: 3-6 symbols
    // per tab, and the cache heats gradually as users click through.
    const symsForCat = CATEGORY_MAP[selectedCategory] || [];
    if (symsForCat.length === 0) return;
    try {
      const r = await axios.get(
        `${API}/market/quotes?symbols=${encodeURIComponent(symsForCat.join(','))}`
      );
      const newQuotes = { ...quotes };
      for (const q of r.data || []) newQuotes[q.symbol] = q;
      setPrevQuotes(quotes);
      setQuotes(newQuotes);
    } catch (e) {
      console.debug('Market category quotes fetch skipped', e?.message);
    }
  };

  const handleTrade = (symbol) => {
    navigate(`/dashboard/webtrader?symbol=${symbol}`);
  };

  const currentSymbols = CATEGORY_MAP[selectedCategory] || [];

  return (
    <section className="py-16 px-6 bg-white border-b border-slate-200" data-testid="market-category-section">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-8">
          <div className="text-xs uppercase tracking-widest text-brand-accent font-bold mb-3">{t('liveMarkets')}</div>
          <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-slate-900">{t('marketCategoriesTitle')}</h2>
          <p className="text-slate-600 mt-2">{t('marketCategoriesSubtitle')}</p>
        </div>

        {/* Category Tabs */}
        <div className="flex flex-wrap gap-2 mb-6 justify-center overflow-x-auto pb-2" data-testid="market-categories">
          {categories.map((cat) => (
            <button
              key={cat.key}
              onClick={() => setSelectedCategory(cat.key)}
              data-testid={`market-category-${cat.key.toLowerCase().replace(/\s+/g, '-')}`}
              className={`px-4 py-2 rounded-full text-sm font-semibold whitespace-nowrap transition-colors ${
                selectedCategory === cat.key
                  ? 'bg-brand-primary text-white'
                  : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
              }`}
            >
              {cat.label}
            </button>
          ))}
        </div>

        {/* Instruments Grid */}
        {currentSymbols.length === 0 ? (
          <div className="text-center text-slate-500 py-8">No instruments available in this category yet.</div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4" data-testid="market-instruments-grid">
            {currentSymbols.map(symbol => {
              const quote = quotes[symbol];
              const prev = prevQuotes[symbol];
              const changeUp = prev && quote && quote.bid > prev.bid;
              const changeDown = prev && quote && quote.bid < prev.bid;
              const isManual = quote?.source === 'manual';
              const unavailable = !quote || quote.unavailable || quote.bid == null;
              return (
                <div key={symbol} className="bg-white border border-slate-200 rounded-xl p-4 hover:shadow-lg hover:border-brand-accent transition-all" data-testid={`market-card-${symbol}`}>
                  <div className="flex justify-between items-start mb-3">
                    <div>
                      <div className="font-bold text-brand-primary">{symbol}</div>
                      <div className="text-[10px] uppercase tracking-widest text-slate-500">
                        {isManual ? 'Manual' : 'Live'}
                      </div>
                    </div>
                    {changeUp && <ArrowUp size={18} className="text-trading-bullish" weight="bold" />}
                    {changeDown && <ArrowDown size={18} className="text-trading-bearish" weight="bold" />}
                  </div>
                  <div className="space-y-1 mb-4">
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-500">{t('bid')}</span>
                      <span className="font-semibold tabular-nums text-trading-bearish">{unavailable ? '—' : quote.bid.toFixed(5)}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-500">{t('ask')}</span>
                      <span className="font-semibold tabular-nums text-trading-bullish">{unavailable ? '—' : quote.ask.toFixed(5)}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-500">{t('spread')}</span>
                      <span className="text-xs text-slate-600 tabular-nums">{unavailable ? '—' : Number(quote.spread).toFixed(5)}</span>
                    </div>
                  </div>
                  <Button size="sm" onClick={() => handleTrade(symbol)} className="w-full" data-testid={`market-trade-${symbol}`}>
                    {t('trade')}
                  </Button>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </section>
  );
};

export default MarketCategoryBar;
